import { Fragment, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeftRight, Check, Copy, Rocket } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { getBreadcrumb, subscribeBreadcrumb } from "@/utils/breadcrumb";
import AjudaEmVideo from "@/components/AjudaEmVideo";
import api from "@/utils/api";
import { dataHora } from "@/utils/dataHora";
import { ThemeToggle } from "./ThemeToggle";

function trilhaDoMenu(grupos, pathname) {  for (const g of grupos) {
    for (const item of g.items) {
      if (item.children) {
        const sub = item.children.find((s) => s.path === pathname);
        if (sub) return [{ label: item.label, path: item.children[0].path }, { label: sub.label }];
      }
      if (item.path === pathname) return [{ label: item.label }];
      if (item.path !== "/admin" && pathname.startsWith(`${item.path}/`)) {
        return [{ label: item.label, path: item.path }, { label: "Detalhe" }];
      }
    }
  }
  return [{ label: "Dashboard" }];
}

export function AdminHeader({ grupos, theme, setTheme }) {
  const { pathname } = useLocation();
  const [override, setOverride] = useState(getBreadcrumb);
  const [copiado, setCopiado] = useState(false);
  const [naoVistas, setNaoVistas] = useState(0);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  useEffect(() => subscribeBreadcrumb(setOverride), []);

  // Contador de atualizações do admin ainda não lidas (zera ao abrir a tela).
  useEffect(() => {
    const buscar = () =>
      api
        .get("/changelog?audiencia=admin&resumo=true")
        .then(({ data }) => {
          setNaoVistas(data.unseen || 0);
          setUltimaAtualizacao(data.ultima_atualizacao || null);
        })
        .catch(() => setNaoVistas(0));
    buscar();
    window.addEventListener("changelog-seen", buscar);
    return () => window.removeEventListener("changelog-seen", buscar);
  }, [pathname]);

  const trilha =
    override?.path === pathname
      ? override.trail.map((label) => ({ label }))
      : trilhaDoMenu(grupos, pathname);

  const textoDaTrilha = `Admin > ${trilha.map((t) => t.label).join(" > ")} (${pathname})`;
  const copiarTrilha = async () => {
    try {
      await navigator.clipboard.writeText(textoDaTrilha);
    } catch {
      const area = document.createElement("textarea");
      area.value = textoDaTrilha;
      document.body.appendChild(area);
      area.select();
      document.execCommand("copy");
      area.remove();
    }
    setCopiado(true);
    setTimeout(() => setCopiado(false), 1600);
  };

  return (
    <header
      className="flex h-16 shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12"
      data-testid="admin-header"
    >
      <div className="flex items-center gap-2 px-4">
        <SidebarTrigger className="-ml-1" data-testid="admin-sidebar-trigger" />
        <Separator orientation="vertical" className="mr-2 h-4" />
        <Breadcrumb data-testid="admin-breadcrumbs">
          <BreadcrumbList>
            <BreadcrumbItem className="hidden md:block">
              <BreadcrumbLink asChild>
                <Link to="/admin">Admin</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            {trilha.map((t, i) => (
              <Fragment key={`${t.label}-${i}`}>
                <BreadcrumbSeparator className="hidden md:block" />
                <BreadcrumbItem>
                  {i === trilha.length - 1 || !t.path ? (
                    <BreadcrumbPage
                      onClick={copiarTrilha}
                      title={`Copiar "${textoDaTrilha}"`}
                      className="cursor-pointer transition-colors hover:text-foreground"
                      data-testid={`admin-breadcrumb-${i}`}
                    >
                      {t.label}
                    </BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <Link to={t.path}>{t.label}</Link>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
              </Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
        <button
          type="button"
          onClick={copiarTrilha}
          title={`Copiar "${textoDaTrilha}"`}
          className="shrink-0 rounded-md p-1 text-muted-foreground/70 transition-colors hover:bg-accent hover:text-foreground"
          data-testid="admin-breadcrumb-copy"
        >
          {copiado ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
        </button>
        {copiado && (
          <span
            className="shrink-0 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700"
            data-testid="admin-breadcrumb-copied"
          >
            copiado
          </span>
        )}
        <AjudaEmVideo />
      </div>
      <div className="ml-auto flex items-center gap-1 px-4">
        <Button variant="ghost" size="sm" asChild className="hidden sm:inline-flex relative">
          <Link
            to="/admin/whats-new"
            data-testid="header-admin-whats-new-btn"
            data-tip={
              ultimaAtualizacao
                ? `Última atualização em ${dataHora(ultimaAtualizacao)}`
                : "Atualizações do painel administrativo"
            }
          >
            <Rocket />
            Atualizações
            {naoVistas > 0 && (
              <span
                className="ml-1 rounded-full bg-sky-600 px-1.5 text-[10px] font-bold leading-4 text-white"
                data-testid="header-admin-whats-new-badge"
              >
                {naoVistas}
              </span>
            )}
          </Link>
        </Button>
        <Button variant="ghost" size="sm" asChild className="hidden sm:inline-flex">
          <Link to="/dashboard" data-testid="header-back-to-tenant-btn">
            <ArrowLeftRight />
            Voltar ao cliente
          </Link>
        </Button>
        <ThemeToggle theme={theme} setTheme={setTheme} />
      </div>
    </header>
  );
}
